import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import apiClient from '../services/webTicketApiClient';
import './trustUserTicketStatus.css'; // Import the stylesheet
import JobManagement from './JobManagement';

function TrustUserTicketStatus() {
  const { jobRunId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [ticketData, setTicketData] = useState(null);
  const [isSessionReady, setIsSessionReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newComments, setNewComments] = useState({});
  const [submitError, setSubmitError] = useState(null);

  // Helper to format possible timestamp shapes safely
  const formatDateTime = (time) => {
    try {
      if (!time) return '-';
      const ts = (time && time.$date) ? time.$date : time;
      const d = new Date(ts);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    } catch (e) {
      return '-';
    }
  };

  useEffect(() => {
    const loadSession = async () => {
      try {
        const response = await apiClient.get('/api/v2/web/auth/session', {
          headers: { 'X-Trust-Session-Refresh': '1' },
        });
        sessionStorage.setItem('trust_web_csrf', response.data.csrfToken);
        setIsSessionReady(true);
      } catch (err) {
        sessionStorage.removeItem('trust_web_csrf');
        navigate(`/login?returnTo=${encodeURIComponent(location.pathname)}`, { replace: true });
      }
    };

    loadSession();
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (!isSessionReady) {
      return;
    }

    if (!jobRunId) {
      setTicketData(null);
      return;
    }

    const controller = new AbortController();

    const fetchTicketDetails = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await apiClient.get(`/api/v2/web/tickets/${jobRunId}`, {
          signal: controller.signal,
        });
        setTicketData(response.data);
      } catch (err) {
        if (!axios.isCancel(err)) {
          setError('Failed to fetch ticket details. Please select a valid ticket.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchTicketDetails();

    return () => {
      controller.abort();
    };
  }, [isSessionReady, jobRunId]);

  const renderCommentText = (text) => {
    if (!text) return '';
    const segments = String(text).split(/(https?:\/\/[^\s]+|www\.[^\s]+)/gi);
    return segments.map((segment, index) => {
      if (!segment) return null;
      const isUrl = /^https?:\/\//i.test(segment) || /^www\./i.test(segment);
      if (!isUrl) {
        return <React.Fragment key={`${segment}-${index}`}>{segment}</React.Fragment>;
      }
      const href = /^www\./i.test(segment) ? `https://${segment}` : segment;
      return (
        <a
          key={`${segment}-${index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#2563eb', textDecoration: 'underline', wordBreak: 'break-all' }}
        >
          {segment}
        </a>
      );
    });
  };

  const handleCommentChange = (taskName, user, text) => {
    setNewComments(prev => ({
      ...prev,
      [taskName]: { ...prev[taskName], [user]: text },
    }));
  };

  const handleAddComment = async (e, taskName, user) => {
    e.preventDefault();
    const commentText = newComments[taskName]?.[user];

    if (!commentText || !commentText.trim()) return;

    setSubmitError(null);

    try {
      const response = await apiClient.post(
        `/api/v2/web/tickets/${ticketData.jobRunId}/tasks/${taskName}/comments`,
        {
          text: commentText,
        }
      );

      const newComment = response.data;

      setTicketData(prevData => ({
        ...prevData,
        tasks: prevData.tasks.map(task => {
          if (task.taskName === taskName) {
            return { ...task, comments: [newComment, ...(task.comments || [])] };
          }
          return task;
        }),
      }));

      handleCommentChange(taskName, user, '');

    } catch (err) {
      console.error("Failed to add comment:", err);
      setSubmitError(
        err.response?.data?.message
        || err.response?.data?.msg
        || "Failed to submit comment. Please try again."
      );
    }
  };

  return (
    <div className="container">
      <JobManagement />

      <div className="section ticket-details">
        {isLoading && <div>Loading Ticket Details...</div>}
        {error && <div style={{ color: 'red' }}>{error}</div>}
        {!jobRunId && !isLoading && <div>Please select a ticket from the list to see the details.</div>}
        
        {ticketData && (
          <>
            <h2>{`Ticket #${ticketData.jobRunId.substring(4, 10)} - ${ticketData.jobTitle}`}</h2>
            
            <div className="meta">
              Status: <span>{ticketData.status}</span>
              <br />
              Created Date: <span>{new Date(ticketData.startTime.$date || ticketData.startTime).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
            </div>
            
            <p>{ticketData.jobDescription}</p>
            
            <div className="comments">
              <h3>Tasks</h3>
              {ticketData.tasks.map((task, index) => {
                const canComment = index === 0;

                return (
                  <div key={index} className="comment">
                    <strong>{`${task.taskName} (Status: ${task.status})`}</strong>
                    <p style={{ margin: '5px 0 15px 0' }}>{task.taskDescription}</p>

                    <div style={{ marginLeft: '20px', marginTop: '10px', fontSize: '0.9em', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                      <strong>Comments:</strong>
                      {task.comments && task.comments.length > 0 ? (
                        task.comments.map((comment, commentIdx) => (
                          <div key={comment.id || commentIdx} style={{ marginTop: '5px' }}>
                            {'• '}{renderCommentText(comment.text)}
                            {comment.userId && <em>{` (by ${comment.userId} at ${formatDateTime(comment.timestamp)})`}</em>}
                          </div>
                        ))
                      ) : (
                        <div style={{ fontStyle: 'italic', color: '#888', marginTop: '5px' }}>No comments yet.</div>
                      )}
                    </div>


                    <div style={{ marginLeft: '20px', marginTop: '15px' }}>
                      <form onSubmit={(e) => handleAddComment(e, task.taskName, 'Customer')} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                        <input 
                          type="text" 
                          placeholder={canComment ? "Add a comment as Customer..." : "Commenting is disabled for this task"}
                          value={newComments[task.taskName]?.Customer || ''}
                          onChange={(e) => handleCommentChange(task.taskName, 'Customer', e.target.value)}
                          style={{ flex: 1, padding: '8px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: canComment ? '#fff' : '#f1f1f1' }}
                          disabled={!canComment}
                        />
                        <button type="submit" style={{ padding: '8px 12px', border: 'none', backgroundColor: canComment ? '#2873f0' : '#ccc', color: 'white', borderRadius: '4px', cursor: canComment ? 'pointer' : 'not-allowed' }} disabled={!canComment}>Add</button>
                      </form>

                      {submitError && <div style={{color: 'red', marginTop: '10px', marginLeft: '5px'}}>{submitError}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TrustUserTicketStatus;